/**
 * The composer (web-plan.md F1+F2): the growing textarea (`field-sizing:
 * content` where the engine has it, a measured fallback where it does not),
 * typing frames on the throttle, the slow-mode countdown (D127/D128), the
 * reply/edit banner, the D131 split notice — and the F2 half: staged
 * attachment chips and the voice recorder (WebCodecs + our muxer, D55) with
 * the client-declared 10-minute cap (D129).
 */
import { For, type JSX, Show, createEffect, createMemo, createSignal, onCleanup } from "solid-js";

import { escapable, escapePending } from "../lib/escape";
import { currentLifetime, isCurrent } from "../lib/lifetime";
import type { Dict } from "../lib/i18n";
import { ORIGINAL_MAX_BYTES } from "../lib/photo";
import { MAX_DURATION_MS, VoiceRecorder, recorderSupported } from "../lib/recorder";
import { parseMarkdown, toMarkdown } from "../lib/wasm";
import type { PreparedSend, StagedFile } from "../store/runtime";
import type { ComposerMode, FeedState } from "../store/types";
import { Icon } from "./ds";

const FIELD_SIZING_SUPPORTED =
  typeof CSS !== "undefined" && CSS.supports?.("field-sizing", "content");

export function Composer(props: {
  feed: FeedState;
  t: Dict;
  limit: number;
  slowmodeS: number;
  admin: boolean;
  lastOwnSentAtMs: number;
  maxMediaBytes: number;
  onSend(text: string, staged: StagedFile[]): void;
  onPrepareSend(): PreparedSend | null;
  onTyping(): void;
  onCancelMode(): void;
  onNotice(text: string): void;
  editSource?: { text: string; entities: import("../gen/dto").Entity[] } | null;
}): JSX.Element {
  const [draft, setDraft] = createSignal("");
  const [staged, setStaged] = createSignal<StagedFile[]>([]);
  const [recording, setRecording] = createSignal<VoiceRecorder | null>(null);
  const [recordMs, setRecordMs] = createSignal(0);
  const [recordLevel, setRecordLevel] = createSignal(0);
  // Which chip has «send as picture / send as file» open (D233), by index.
  const [sendAsFor, setSendAsFor] = createSignal<number | null>(null);
  escapable(() => sendAsFor() !== null, () => setSendAsFor(null));
  let disposed = false;
  onCleanup(() => {
    disposed = true;
    recording()?.cancel();
  });
  let input!: HTMLTextAreaElement;
  let filePick!: HTMLInputElement;

  createEffect(() => {
    const mode = props.feed.mode;
    if (mode.t === "editing" && props.editSource) {
      const md = toMarkdown(props.editSource.text, props.editSource.entities);
      setDraft(md);
      queueMicrotask(() => {
        input.focus();
        autoResize();
      });
    }
  });

  const autoResize = () => {
    if (FIELD_SIZING_SUPPORTED) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(180, Math.max(38, input.scrollHeight))}px`;
  };

  const parsedUnits = createMemo(() => {
    const raw = draft();
    if (raw === "") return 0;
    try {
      return parseMarkdown(raw).text.length;
    } catch {
      return raw.length;
    }
  });
  const over = () => parsedUnits() > props.limit;
  const splitParts = () => Math.min(10, Math.ceil(parsedUnits() / Math.max(1, props.limit)));

  const [nowS, setNowS] = createSignal(Math.floor(Date.now() / 1000));
  const timer = setInterval(() => {
    setNowS(Math.floor(Date.now() / 1000));
    const rec = recording();
    if (rec) {
      setRecordMs(rec.elapsedMs());
      setRecordLevel(rec.currentLevel());
    }
  }, 250);
  onCleanup(() => clearInterval(timer));
  const slowWait = createMemo(() => {
    if (props.admin || props.slowmodeS === 0 || props.lastOwnSentAtMs === 0) return 0;
    const since = nowS() - Math.floor(props.lastOwnSentAtMs / 1000);
    return Math.max(0, props.slowmodeS - since);
  });

  const stage = (files: FileList | File[]) => {
    const next = [...staged()];
    for (const file of files) {
      if (next.length >= 10) break; // ten attachments per message (§4)
      const kind = kindOf(file);
      // The per-channel media ceiling (D128): refused in the composer, the
      // server is the second line.
      if (file.size > props.maxMediaBytes && kind !== "file") {
        props.onNotice(
          `${file.name}: ${Math.floor(file.size / (1024 * 1024))} MiB is over this channel's limit`,
        );
        continue;
      }
      const item: StagedFile = { file, kind };
      if (kind === "photo") item.previewUrl = URL.createObjectURL(file);
      next.push(item);
    }
    setStaged(next);
  };

  const unstage = (at: number) => {
    const next = [...staged()];
    const [removed] = next.splice(at, 1);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    setSendAsFor(null);
    setStaged(next);
  };

  /** «Send as picture» / «send as file» for the chip the menu belongs to. */
  const chooseAsFile = (asFile: boolean) => {
    const at = sendAsFor();
    setSendAsFor(null);
    if (at === null) return;
    const item = staged()[at];
    if (!item) return;
    // Said now, while the chip is still on screen and the choice can be taken
    // back — not after five megabytes have gone up.
    if (asFile && item.file.size > ORIGINAL_MAX_BYTES) {
      props.onNotice(props.t.originalOverLimit(ORIGINAL_MAX_BYTES));
      return;
    }
    const next = [...staged()];
    next[at] = { ...item, asFile };
    setStaged(next);
  };

  const submit = () => {
    const text = draft();
    if (text.trim() === "" && staged().length === 0) return;
    if (slowWait() > 0) return;
    props.onSend(text, staged());
    setDraft("");
    setStaged([]);
    if (!FIELD_SIZING_SUPPORTED) queueMicrotask(autoResize);
  };

  const startRecording = async () => {
    if (!recorderSupported()) {
      props.onNotice("voice notes need a browser with WebCodecs");
      return;
    }
    const lifetime = currentLifetime();
    const rec = new VoiceRecorder();
    setRecording(rec);
    try {
      await rec.start(() => void finishRecording());
      if (disposed || !isCurrent(lifetime)) {
        rec.cancel();
        return;
      }
    } catch (e) {
      rec.cancel();
      if (disposed || !isCurrent(lifetime) || recording() !== rec) return;
      setRecording(null);
      props.onNotice(
        e instanceof DOMException && e.name === "AbortError"
          ? props.t.recordingCancelled()
          : e instanceof Error ? e.message : String(e),
      );
    }
  };

  const finishRecording = async () => {
    const rec = recording();
    if (!rec) return;
    const lifetime = currentLifetime();
    const send = props.onPrepareSend();
    setRecording(null);
    const note = await rec.stop();
    if (!isCurrent(lifetime) || !send) return;
    if (!note || note.durationMs < 400) return; // a tap is not a message
    const file = new File([note.ogg as BlobPart], "voice.ogg", { type: "audio/ogg" });
    send("", [
      {
        file,
        kind: "voice",
        waveform: note.waveformBase64,
        durationMs: Math.min(note.durationMs, MAX_DURATION_MS),
      },
    ]);
  };

  const mode = () => props.feed.mode;

  return (
    <div class="composer surface-pane">
      <Show when={mode().t !== "new"}>
        <div class="composer__mode">
          <Icon
            name={mode().t === "editing" ? "edit" : "reply"}
            size={15}
            color="var(--accent)"
          />
          <div class="composer__mode-column">
            <span class="composer__mode-title">
              {mode().t === "editing" ? props.t.menuEdit() : props.t.menuReply()}
            </span>
            <span class="composer__mode-text">#{modeSeq(mode())}</span>
          </div>
          <button type="button" class="notice-bar__close" onClick={() => props.onCancelMode()}>
            <Icon name="close" size={16} />
          </button>
        </div>
      </Show>

      <Show when={props.feed.error}>
        <div class="composer__error">{props.feed.error}</div>
      </Show>

      <Show
        when={!recording()}
        fallback={
          <div class="voice-recorder">
            <button
              type="button"
              class="composer__icon"
              onClick={() => {
                recording()?.cancel();
                setRecording(null);
              }}
            >
              <Icon name="delete" size={22} />
            </button>
            <span class="voice-recorder__dot" />
            <span class="voice-recorder__time">
              {Math.floor(recordMs() / 60000)}:
              {String(Math.floor((recordMs() % 60000) / 1000)).padStart(2, "0")}
            </span>
            <span class="voice-recorder__meter">
              <i style={`transform:scaleX(${recordLevel().toFixed(3)})`} />
            </span>
            <button type="button" class="composer__send-button" onClick={() => void finishRecording()}>
              <Icon name="arrow_upward" size={18} weight={600} />
            </button>
          </div>
        }
      >
        <Show when={staged().length > 0}>
          <div class="attachment-chips-row">
            <div class="attachment-chips">
              <For each={staged()}>
                {(item, i) => (
                  <div class="attachment-chip">
                    <Show
                      when={item.previewUrl && !item.asFile}
                      fallback={
                        <Icon
                          name={chipIcon(item.asFile ? "file" : item.kind)}
                          size={22}
                          class="attachment-chip__kind"
                        />
                      }
                    >
                      <img
                        src={item.previewUrl}
                        alt=""
                        style="width:100%;height:100%;object-fit:cover;border-radius:inherit"
                      />
                    </Show>
                    {/* Only a picture has two readings (D233): a `.zip` is a
                        file whatever anybody picks. */}
                    <Show when={item.kind === "photo"}>
                      <button
                        type="button"
                        class="attachment-chip__as"
                        title={props.t.sendAs()}
                        onClick={() => setSendAsFor(sendAsFor() === i() ? null : i())}
                      >
                        <Icon name="expand_more" size={12} />
                      </button>
                    </Show>
                    <button type="button" class="attachment-chip__close" onClick={() => unstage(i())}>
                      <Icon name="close" size={12} />
                    </button>
                  </div>
                )}
              </For>
            </div>
            {/* Outside `.attachment-chip`, which clips to its rounded 64 px tile. */}
            <Show when={sendAsFor() !== null}>
              <div class="echo-popover attachment-chip__menu">
                <div class="echo-menu" role="menu">
                  <button type="button" class="echo-menu__item" onClick={() => chooseAsFile(false)}>
                    <Icon name="image" size={18} />
                    {`${props.t.sendAsPicture()} · ${props.t.sendAsPictureHint()}`}
                  </button>
                  <button type="button" class="echo-menu__item" onClick={() => chooseAsFile(true)}>
                    <Icon name="description" size={18} />
                    {`${props.t.sendAsFile()} · ${props.t.sendAsFileHint(ORIGINAL_MAX_BYTES)}`}
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </Show>
        <div class="composer__row">
          <button
            type="button"
            class="composer__icon"
            disabled={staged().length >= 10}
            onClick={() => filePick.click()}
          >
            <Icon name="attach_file" size={22} />
          </button>
          <textarea
            ref={input}
            class="composer__input"
            rows="1"
            spellcheck={false}
            placeholder={
              slowWait() > 0 ? props.t.slowModeWait(slowWait()) : props.t.composerPlaceholder()
            }
            value={draft()}
            onInput={(e) => {
              setDraft(e.currentTarget.value);
              props.onTyping();
              autoResize();
            }}
            onPaste={(e) => {
              const files = [...(e.clipboardData?.files ?? [])];
              if (files.length > 0) {
                e.preventDefault();
                stage(files);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
              // Not while something floats over the page: a menu opened
              // without taking the keyboard leaves the box focused, and that
              // press is the menu's (`lib/escape`).
              if (e.key === "Escape" && mode().t !== "new" && !escapePending()) {
                e.preventDefault();
                props.onCancelMode();
                setDraft("");
              }
            }}
          />
          <Show when={over()}>
            <span class="composer__counter composer__counter--over">
              {props.t.messageTooLongSplit(splitParts())}
            </span>
          </Show>
          <Show
            when={draft().trim() !== "" || staged().length > 0}
            fallback={
              <button type="button" class="composer__icon" onClick={() => void startRecording()}>
                <Icon name="mic" size={22} />
              </button>
            }
          >
            <button
              type="button"
              class="composer__send-button"
              disabled={slowWait() > 0}
              onClick={submit}
            >
              <Icon
                name={mode().t === "editing" ? "done" : "arrow_upward"}
                size={18}
                weight={600}
              />
            </button>
          </Show>
          <input
            ref={filePick}
            type="file"
            multiple
            style="display:none"
            onChange={(e) => {
              if (e.currentTarget.files) stage(e.currentTarget.files);
              e.currentTarget.value = "";
            }}
          />
        </div>
      </Show>
    </div>
  );
}

function kindOf(file: File): import("../gen/dto").FileKind {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type === "video/mp4") return "video";
  if (file.type === "audio/ogg") return "voice";
  return "file";
}

function chipIcon(kind: string): string {
  switch (kind) {
    case "video":
      return "movie";
    case "voice":
      return "mic";
    default:
      return "description";
  }
}

function modeSeq(mode: ComposerMode): number {
  return mode.t === "new" ? 0 : mode.seq;
}
